import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tag, Home, MapPin, Calendar, X } from 'lucide-react';
import { EscortProfile, EscortRate } from '@/types';
import { Badge } from "@/components/ui/badge";
import { useTranslation } from 'react-i18next';

// Helper function to format duration
const formatDuration = (duration: string | number | undefined, t: (key: string, options?: { count: number }) => string): string => {
  if (duration === undefined || duration === null || duration === '') return '';
  
  // Check if it's purely numeric (or a string representing a number)
  const durationNum = typeof duration === 'string' ? parseInt(duration, 10) : duration;
  const isNumeric = !isNaN(durationNum) && duration.toString() === durationNum.toString();

  if (isNumeric && durationNum > 0) {
    // Assuming numeric duration is in minutes
    const hours = durationNum / 60;
    
    if (hours === 1) {
      return `1 ${t('common:hour', { count: 1 })}`;
    }
    if (hours > 1 && Number.isInteger(hours)) {
      return `${hours} ${t('common:hours', { count: hours })}`;
    }
    // Handle 30 minutes explicitly?
    if (durationNum === 30) return `30 ${t('common:minutes', { count: 30 })}`; 
    // Fallback to minutes if numeric but not exactly 1 or more full hours
    return `${durationNum} ${t('common:minutes', { count: durationNum })}`;
  } else if (typeof duration === 'string') {
    // If it's a non-empty string but not purely numeric, return it as is
    return duration; 
  }
  
  return ''; // Return empty for other cases (like non-string, non-numeric)
};

// Define standard rate items to display on the profile page
// We use 'key' to potentially match against rate.duration or rate.description later
// We use 'durationMinutes' for potential matching if needed, or null for descriptive items
const standardRateItems = [
  { key: '30', durationMinutes: 30, labelKey: 'profile:rate_30min', defaultLabel: '30 min' },
  { key: '60', durationMinutes: 60, labelKey: 'profile:rate_1hour', defaultLabel: '1 heure' },
  { key: '120', durationMinutes: 120, labelKey: 'profile:rate_2hours', defaultLabel: '2 heures' },
  // Add descriptive keys based on common descriptions (adjust keys as needed)
  { key: 'Nuit complète', durationMinutes: null, labelKey: 'profile:rate_overnight', defaultLabel: 'Nuit complète' },
  { key: 'Week-end', durationMinutes: null, labelKey: 'profile:rate_weekend', defaultLabel: 'Week-end' },
];

interface RatesSectionProps {
  profile: EscortProfile;
}

// Renders the list for a category, showing default items with prices or placeholders
const ProfileRateList: React.FC<{ categoryRates: EscortRate[], t: Function }> = ({ categoryRates, t }) => (
  <div className="space-y-2">
    {standardRateItems.map((item) => {
      // Find a matching rate in the escort's actual rates for this category
      const matchingRate = categoryRates.find(rate => 
        // Match logic: Check duration if numeric, otherwise check description (case-insensitive)
        (item.durationMinutes !== null && 
          (rate.duration === item.durationMinutes || 
           (typeof rate.duration === 'string' && parseInt(rate.duration, 10) === item.durationMinutes))) || 
        (item.durationMinutes === null && rate.description?.toLowerCase() === item.defaultLabel.toLowerCase())
      );

      const displayLabel = t(item.labelKey, { defaultValue: item.defaultLabel });

      return (
        <div key={item.key} className="flex justify-between items-center text-sm">
          <span>{displayLabel}</span>
          {matchingRate ? (
            <Badge variant="secondary" className="whitespace-nowrap">
              {matchingRate.price} {matchingRate.currency || 'EUR'}
            </Badge>
          ) : (
            <span className="text-muted-foreground text-lg">-</span> // Placeholder for non-offered rates
          )}
        </div>
      );
    })}
  </div>
);

export const RatesSection: React.FC<RatesSectionProps> = ({ profile }) => {
  const { t } = useTranslation(['common', 'profile']);
  
  // Fonction pour organiser les tarifs par catégorie
  const organizeRatesByCategory = (): { incall: EscortRate[], outcall: EscortRate[], special: EscortRate[] } => {
    const organized: { incall: EscortRate[], outcall: EscortRate[], special: EscortRate[] } = {
      incall: [],
      outcall: [],
      special: []
    };

    // Format 1: Nouveau format avec champ 'category'
    if (profile.rates && Array.isArray(profile.rates)) {
      profile.rates.forEach(rate => {
        // Provide a default category if it's missing, making access safer
        const category = (rate as EscortRate).category || 'incall'; 
        if (organized[category as keyof typeof organized]) {
          organized[category as keyof typeof organized].push(rate as EscortRate);
        } else {
          // Handle potential unknown categories if necessary, or default to 'special'?
          organized.special.push(rate as EscortRate);
        }
      });
    } 
    // Format 2: Ancien format avec structure imbriquée
    else if (profile.rates && typeof profile.rates === 'object' && !Array.isArray(profile.rates)) {
      // Adapt the old format extraction to fit EscortRate structure
      Object.entries(profile.rates).forEach(([category, categoryRates]) => {
        if (typeof categoryRates === 'object' && categoryRates !== null && organized[category as keyof typeof organized]) {
          Object.entries(categoryRates).forEach(([key, value]) => {
            // Try to create a meaningful description if not present
            let description = t(`profile.rates_${key}`, { defaultValue: key.replace('_', ' ') });
            let duration: string | number | undefined = undefined;
            // Attempt to extract numeric duration if key implies it (e.g., "1_hour", "90_min")
            const keyLower = key.toLowerCase();
            if (keyLower.includes('hour')) duration = parseInt(key) * 60;
            else if (keyLower.includes('min')) duration = parseInt(key);
            else if (keyLower.includes('nuit')) description = t('profile:rates_overnight', {defaultValue: 'Nuitée'}); // Handle overnight case for description
            
            organized[category as keyof typeof organized].push({
              id: `${category}_${key}`,
              description: description,
              duration: duration,
              price: typeof value === 'number' ? value : 0,
              currency: 'EUR', // Assuming EUR for old format
              category: category, // Use the key from the old structure as category
              visibility: 'public' // Assuming public for old format
            });
          });
        }
      });
    }

    return organized;
  };

  const categorizedRates = organizeRatesByCategory();
  
  const hasAnyRates = 
    categorizedRates.incall.length > 0 || 
    categorizedRates.outcall.length > 0 || 
    categorizedRates.special.length > 0;
    
  // Don't render the section at all if profile has no rate info whatsoever
  // This assumes organizeRatesByCategory returns empty arrays if profile.rates is null/undefined
  if (!profile.rates) { 
    return null; 
  }

  return (
    <Card className="mb-6 border-pink-100 overflow-hidden">
      <div className="bg-pink-50 py-3 px-6 border-b border-pink-100">
        <h3 className="text-lg font-medium text-pink-700 flex items-center">
          <Tag size={18} className="mr-2 text-pink-500" />
          {t('profile:rates')}
        </h3>
      </div>
      
      {/* Grid structure for Incall/Outcall */}
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-0`}> 
        
        {/* Incall Section */}
        <div className="p-6 md:border-r border-pink-100"> 
          <h4 className="text-md font-semibold text-pink-600 flex items-center mb-3">
            <Home size={16} className="mr-2" />
            {t('profile:rates_incall', { defaultValue: 'Incall' })}
          </h4>
          <ProfileRateList categoryRates={categorizedRates.incall} t={t} />
        </div>

        {/* Outcall Section */}
        <div className="p-6 border-t border-pink-100 md:border-t-0"> 
          <h4 className="text-md font-semibold text-pink-600 flex items-center mb-3">
            <MapPin size={16} className="mr-2" />
            {t('profile:rates_outcall', { defaultValue: 'Outcall' })}
          </h4>
          <ProfileRateList categoryRates={categorizedRates.outcall} t={t} />
        </div>
        
      </div>

      {/* Special Rates Section (Only if rates exist) */}
      {categorizedRates.special.length > 0 && (
        <div className="p-6 border-t border-pink-100">
          <h4 className="text-md font-semibold text-pink-600 flex items-center mb-3">
            <Calendar size={16} className="mr-2" />
            {t('profile:rates_special', { defaultValue: 'Spécial' })}
          </h4>
          {/* Display special rates as they are defined */}
           <div className="space-y-2">
            {categorizedRates.special.map((rate, index) => {
              // Use description or formatted duration for special rates
              const displayLabel = rate.description || formatDuration(rate.duration, t) || 'Service spécial';
              return (
                <div key={rate.id || index} className="flex justify-between items-center text-sm">
                  <span>{displayLabel}</span>
                  <Badge variant="secondary" className="whitespace-nowrap">
                    {rate.price} {rate.currency || 'EUR'}
                  </Badge>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Note Section */}
       <div className="p-6 pt-4 border-t border-pink-100 md:border-t-0 md:pt-6"> {/* Adjust padding/border */} 
        <p className="text-xs text-muted-foreground">
          {t('profile:rates_note', { defaultValue: 'Les prix peuvent varier. Contactez pour plus de détails.' })}
        </p>
      </div>

    </Card>
  );
}; 